/**
 * useServiceOrderStock - Estoque na conclusão da OS (Fase 9)
 *
 * - useServiceOrderStockCandidates: itens que podem ser instalados (ativos no
 *   estoque do prestador) e retirados (no veículo da OS), conforme o tipo.
 * - useServiceOrderStockMovements: itens movimentados pela OS (aba Movimentações).
 * A gravação acontece em `change_service_order_status` (função única do estoque).
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-11 v1.6.0 - Sem polling de 15s; tempo real via useTenantStockRealtime (canal único).
 *   RELEASE: 2026-10-10 v1.5.0 - BR-OS-038: "Corrigir equipamento" vira troca (correction_remove /
 *            correction_install); validação exige item a instalar e item a remover se houver.
 *   RELEASE: 2026-10-10 v1.4.0 - Candidatos sempre reconsultam ao abrir, atualizam a cada 15s
 *            e por Realtime (stock_movements do tenant) enquanto o modal está aberto;
 *            `pruneSelection` remove seleções que deixaram de estar disponíveis.
 *   RELEASE: 2026-10-10 v1.3.0 - BR-OS-034..037: modo correção (OS "Em correção").
 *            Candidatos trazem `correction` e os itens desta OS (revert_install /
 *            revert_uninstall); seleção aceita listas de correção e
 *            `validateCorrectionSelection` valida "Manter" x "Corrigir" com motivo.
 *   RELEASE: 2026-10-09 v1.2.0 - useServiceOrderStockMovements retorna
 *            StockMovementRow[] (formato oficial de get_stock_movements) para
 *            alimentar a StockMovementsTable na aba Movimentações do modal.
 *   RELEASE: 2026-10-09 v1.1.0 - Candidatos detalham elegibilidade, situações,
 *            modelo do rastreador e plano do chip para o seletor premium.
 *   RELEASE: 2026-10-08 v1.0.0 - Criação inicial.
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useTenantStockRealtime } from '@/hooks/realtime/useTenantStockRealtime';
import { supabase } from '@/integrations/supabase/client';
import type { StockMovementRow } from '@/hooks/useStockMovements';

export * from '@/lib/serviceOrderStockRules';
import { type StockCandidates } from '@/lib/serviceOrderStockRules';

// Preserva o `this` do cliente (supabase.rpc usa this.rest)
const rpc = (fn: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (f: string, a: Record<string, unknown>) => Promise<{ data: any; error: any }>).call(supabase, fn, args);

export function useServiceOrderStockCandidates(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['service-order-stock', id, 'candidates'],
    enabled: !!id && enabled,
    staleTime: 0,
    // v1.4.0: sempre busca a lista atual ao abrir e atualiza enquanto aberto
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await rpc('get_service_order_stock_candidates', { p_id: id, p_tenant_hint: null });
      if (error) throw error;
      return data as StockCandidates;
    },
  });
}

/**
 * v1.4.0: Realtime enquanto o modal de conclusão está aberto — qualquer nova
 * movimentação do tenant (outra pessoa, aba ou prestador) atualiza os candidatos.
 */
export function useServiceOrderStockRealtime(id: string | null, enabled: boolean) {
  // v1.6.0: canal único compartilhado por tenant (useTenantStockRealtime)
  useTenantStockRealtime([['service-order-stock', id]], enabled && !!id);
}

export function useServiceOrderStockMovements(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['service-order-stock', id, 'movements'],
    enabled: !!id && enabled,
    queryFn: async () => {
      const { data, error } = await rpc('get_service_order_stock_movements', { p_id: id, p_tenant_hint: null });
      if (error) throw error;
      // v1.2.0: a RPC retorna o formato oficial StockMovementRow (mesmo de get_stock_movements)
      return (data ?? []) as StockMovementRow[];
    },
  });
}
